import {
	IconCalendarEvent,
	IconDownload,
	IconPlayerPlay,
	IconSchool,
	IconTicket,
	type TablerIcon,
} from "@tabler/icons-react"
import { type CSSProperties, type FC, forwardRef } from "react"

import { type LaunchedProduct, type Milestone } from "@forento/core/models/milestone"
import { type ProductType } from "@forento/core/models/product"
import { toPriceString } from "@forento/core/utilities/currency"
import { getCountMilestoneCopy } from "@forento/core/utilities/milestone"
import { cn } from "@forento/ui/utilities/tailwind"

import { useBrand } from "~/contexts/BrandContext"
import { usePlatform } from "~/contexts/UserContext"

import { type CountMilestone, type MilestoneFormat, productTypeLabels } from "./utilities"

// Kept to the top right so they never overlap the text
const stars = [
	[56, 6, 0.6],
	[71, 16, 0.4],
	[88, 9, 0.8],
	[80, 30, 0.5],
	[94, 24, 0.6],
	[64, 40, 0.4],
	[93, 46, 0.5],
] as const

const productTypeIcons: Record<ProductType, TablerIcon> = {
	course: IconSchool,
	video: IconPlayerPlay,
	downloadable: IconDownload,
	session: IconCalendarEvent,
	event: IconTicket,
}

type Props = { milestone: Milestone; format: MilestoneFormat; className?: string }

const MilestoneCard = forwardRef<HTMLDivElement, Props>(({ milestone, format, className }, ref) => {
	const platform = usePlatform()!
	const brand = useBrand()
	const domain = platform.domain.landingPage ?? platform.domain.platform
	const color = platform.primaryColor.value
	const onColor = isLightColor(color) ? "#12062e" : "#ffffff"

	return (
		<div
			ref={ref}
			className={cn(
				"@container relative flex flex-col overflow-hidden bg-white text-left text-white select-none",
				format === "square" ? "aspect-square" : "aspect-9/16",
				className,
			)}
			style={
				{
					"--milestone-color": color,
					"--milestone-on-color": onColor,
				} as CSSProperties
			}
		>
			{/* The dark background stops above the white footer so rounded corners don't show a dark fringe */}
			<div
				className="relative flex flex-1 flex-col p-[7cqw]"
				style={{
					backgroundColor: `color-mix(in srgb, ${color} 18%, #07060d)`,
					backgroundImage: `radial-gradient(circle at 15% 0%, color-mix(in srgb, ${color} 55%, transparent), transparent 55%), radial-gradient(circle at 100% 100%, color-mix(in srgb, ${color} 35%, transparent), transparent 50%)`,
				}}
			>
				{stars.map(([left, top, opacity]) => (
					<div
						key={`${left}-${top}`}
						className="absolute aspect-square w-[0.9cqw] rounded-full bg-white"
						style={{ left: `${left}%`, top: `${top}%`, opacity }}
					/>
				))}
				<div className="flex items-center gap-[3cqw]">
					{platform.logo !== null ? (
						<img
							src={platform.logo.filePath}
							alt=""
							className={cn(
								"rounded-[1.5cqw] bg-white object-contain p-[1cqw]",
								platform.logo.isIcon ? "size-[10cqw]" : "h-[10cqw] max-w-[30cqw]",
							)}
						/>
					) : (
						<div
							className="flex size-[10cqw] shrink-0 items-center justify-center rounded-[1.5cqw] text-[5.5cqw] font-bold"
							style={{ backgroundColor: color, color: onColor }}
						>
							{platform.name.trim().charAt(0).toUpperCase()}
						</div>
					)}
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-[5.2cqw] leading-tight font-bold">{platform.name}</span>
						<span className="truncate text-[3.4cqw] text-white/60">{domain}</span>
					</div>
				</div>
				{milestone.type === "product-launch" ? (
					<LaunchContent product={milestone.product} format={format} />
				) : (
					<CountContent milestone={milestone} format={format} platformName={platform.name} />
				)}
			</div>

			<div className="relative flex items-center justify-between gap-[4cqw] bg-white px-[7cqw] py-[4cqw] text-[#12062e]">
				<img src={brand.logoUrl} alt={brand.name} className="h-[5.5cqw]" />
				<div className="flex flex-col items-end text-right leading-snug">
					<span className="text-[3.6cqw] font-semibold">Build your own academy</span>
					<span className="text-[3.4cqw] font-medium text-[#12062e]/60">forento.io</span>
				</div>
			</div>
		</div>
	)
})

const Kicker: FC<{ children: string }> = ({ children }) => (
	<span className="mb-[3cqw] w-fit rounded-[1.5cqw] bg-white/12 px-[2.5cqw] py-[1cqw] text-[3.6cqw] font-semibold text-white">
		{children}
	</span>
)

const Planet: FC<{ format: MilestoneFormat }> = ({ format }) => (
	<>
		<div
			className={cn(
				"absolute rounded-full",
				format === "square"
					? "-right-[10cqw] bottom-[8cqw] size-[46cqw]"
					: "-right-[14cqw] bottom-[22cqw] size-[70cqw]",
			)}
			style={{
				backgroundImage:
					"radial-gradient(circle at 30% 28%, color-mix(in srgb, var(--milestone-color) 30%, white), var(--milestone-color) 55%, color-mix(in srgb, var(--milestone-color) 55%, black))",
			}}
		/>
		<div
			className={cn(
				"absolute rounded-[50%] border-[1.6cqw] border-white/25",
				format === "square"
					? "-right-[22cqw] bottom-[24cqw] h-[16cqw] w-[70cqw]"
					: "-right-[30cqw] bottom-[46cqw] h-[22cqw] w-[104cqw]",
			)}
			style={{ transform: "rotate(-18deg)" }}
		/>
	</>
)

const CountContent: FC<{ milestone: CountMilestone; format: MilestoneFormat; platformName: string }> = ({
	milestone,
	format,
	platformName,
}) => {
	const copy = getCountMilestoneCopy(milestone, platformName)

	return (
		<>
			<Planet format={format} />
			<div className={cn("relative flex flex-col", format === "square" ? "mt-auto" : "my-auto pb-[20cqw]")}>
				<Kicker>Milestone unlocked</Kicker>
				<span
					className={cn(
						"leading-none font-extrabold tracking-tight",
						copy.value.length > 5 ? "text-[17cqw]" : "text-[22cqw]",
					)}
					style={{ color: "color-mix(in srgb, var(--milestone-color) 35%, white)" }}
				>
					{copy.value}
				</span>
				<span className="text-[7cqw] leading-tight font-semibold">{copy.label}</span>
			</div>
		</>
	)
}

const LaunchContent: FC<{ product: LaunchedProduct; format: MilestoneFormat }> = ({ product, format }) => {
	const Icon = productTypeIcons[product.type]
	const kicker = `Just launched · New ${productTypeLabels[product.type]}`
	const thumbnail = (
		<div
			className={cn(
				"overflow-hidden rounded-[2.5cqw] border-[0.8cqw] border-white/90 shadow-[0_3cqw_8cqw_rgba(0,0,0,0.45)]",
				format === "square" ? "absolute -right-[4cqw] -bottom-[3cqw] w-[58cqw]" : "w-full",
			)}
			style={{ transform: format === "square" ? "rotate(-5deg)" : "rotate(-2deg)" }}
		>
			{product.thumbnailUrl !== null ? (
				<img src={product.thumbnailUrl} alt="" className="aspect-video w-full object-cover" />
			) : (
				<div
					className="flex aspect-video w-full items-center justify-center"
					style={{ backgroundColor: "var(--milestone-color)", color: "var(--milestone-on-color)" }}
				>
					<Icon className="size-[40%] stroke-[1.5]" />
				</div>
			)}
		</div>
	)
	const priceTag = (
		<span
			className="w-fit rounded-full px-[3cqw] py-[1.2cqw] text-[4cqw] font-bold"
			style={{ backgroundColor: "var(--milestone-color)", color: "var(--milestone-on-color)" }}
		>
			{product.price !== null ? toPriceString(product.price) : "Free"}
		</span>
	)

	if (format === "square") {
		return (
			<div className="relative mt-[4cqw] flex flex-1 flex-col">
				<Kicker>{kicker}</Kicker>
				<span className="line-clamp-2 text-[6.5cqw] leading-[1.1] font-extrabold tracking-tight">
					{product.title}
				</span>
				<div className="mt-auto">{priceTag}</div>
				{thumbnail}
			</div>
		)
	}

	return (
		<div className="relative my-auto flex flex-col gap-[7cqw]">
			{thumbnail}
			<div className="flex flex-col">
				<Kicker>{kicker}</Kicker>
				<span className="line-clamp-3 text-[9cqw] leading-[1.1] font-extrabold tracking-tight">
					{product.title}
				</span>
				{product.shortDescription !== null && (
					<span className="mt-[3cqw] line-clamp-3 text-[4.4cqw] leading-snug text-white/75">
						{product.shortDescription}
					</span>
				)}
				<div className="mt-[5cqw]">{priceTag}</div>
			</div>
		</div>
	)
}

// Text placed on the academy color must stay readable on light colors such as yellow
function isLightColor(hex: string) {
	const digits = hex.replace("#", "")
	const value = Number.parseInt(
		digits.length === 3 ? [...digits].map(digit => digit + digit).join("") : digits.slice(0, 6),
		16,
	)
	if (Number.isNaN(value)) return false
	const [red, green, blue] = [(value >> 16) & 255, (value >> 8) & 255, value & 255]
	return (red * 299 + green * 587 + blue * 114) / 1000 > 160
}

export default MilestoneCard
